import { useContext, useEffect, useMemo, useState } from "react";

import WorkerInitials from "../../../components/ui/workers/WorkerInitials";
import {
  ClipboardPaste,
  DocumentDuplicate,
  Pencil,
  Refresh,
} from "../../../components/icons/svg/outline";
import { ScheduleContext } from "../../../context/domain/planning/ScheduleContext";
import { PLANNING_TOOLTIP_MESSAGES } from "../../../config";
import { sameScheduleBlockId } from "../schedule/model/scheduleIds";
import { scheduleCrewIdListsEqual } from "../schedule/model/scheduleCrewClipboard";
import { toLocalCalendarDay, toLocalDateKey } from "../../../utils/datetime/core";

/**
 * Full-bleed confirm layer for paste that would replace an existing crew assignment.
 * Used by {@link CrewCard} and schedule scope inline paste controls.
 *
 * @param {Object} props
 * @param {boolean} props.visible
 * @param {(e: import('react').MouseEvent) => void|Promise<void>} props.onConfirm
 * @param {(e: import('react').MouseEvent) => void} props.onCancel
 * @param {boolean} [props.busy=false]
 */
export function PasteCrewOverwriteOverlay({ visible, onConfirm, onCancel, busy = false }) {
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (!visible) {
      setEntered(false);
      return undefined;
    }
    const id = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => setEntered(true));
    });
    return () => window.cancelAnimationFrame(id);
  }, [visible]);

  if (!visible) {
    return null;
  }

  return (
    <div
      className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-1 rounded-md border border-gray-200/90 bg-white/[0.96] p-3 shadow-[inset_0_0_0_1px_rgba(0,0,0,0.04)] backdrop-blur-[2px] transition-[opacity,transform] duration-200 ease-out"
      style={{
        opacity: entered ? 1 : 0,
      }}
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="paste-crew-overwrite-title"
    >
      <p
        id="paste-crew-overwrite-title"
        className="text-center text-xs font-semibold leading-snug text-gray-800"
      >
        Replace existing crew assignment with copied crew?
      </p>
      <div className="flex w-full max-w-[16rem] gap-2">
        <button
          type="button"
          className="h-6 flex-1 rounded-md border border-gray-300 bg-white px-3 text-sm font-semibold text-gray-800 shadow-sm transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:pointer-events-none disabled:opacity-50"
          disabled={busy}
          onClick={(e) => {
            e.stopPropagation();
            onCancel(e);
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          className="h-6 flex-1 rounded-md bg-primary px-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-secondary-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:pointer-events-none disabled:opacity-60"
          disabled={busy}
          onClick={(e) => {
            e.stopPropagation();
            void onConfirm(e);
          }}
        >
          {busy ? "Applying…" : "Confirm"}
        </button>
      </div>
    </div>
  );
}

/**
 * Shows a scope's crew as worker initials with optional edit, copy, paste, and
 * revert actions. Used on schedule scope bars/modals and dispatch crew editing.
 * When `scheduleBlockId` is set, worker data is read from {@link ScheduleContext}.
 *
 * @param {Object} props
 * @param {string|number} [props.scheduleBlockId] — When set with {@link ScheduleContext}, load `worker_assignment` from that scope block in context.
 * @param {string} props.title — e.g. calendar day label in ScopeCard, or "Crew:" / "Default crew" in modals
 * @param {Array<Object>} [props.workers=[]] — used when {@link crewRows} is omitted and `scheduleBlockId` is not used
 * @param {Array<{ worker: Object, kind?: 'present'|'removed'|'added' }>} [props.crewRows]
 * @param {string} [props.editTooltip] — native tooltip for the edit (pencil) icon
 * @param {(e: import('react').MouseEvent) => void} [props.onEditClick]
 * @param {string} [props.revertTooltip="Revert to default crew"] — when `variant` is `override` and `onRevertToDefaultClick` is set
 * @param {(e: import('react').MouseEvent) => void|Promise<void>} [props.onRevertToDefaultClick] — e.g. clear per-day override (shown beside pencil)
 * @param {(e: import('react').MouseEvent) => void} [props.onCopyCrewClick]
 * @param {boolean} [props.showPasteCrew] — when true, show paste icon (clipboard has a crew)
 * @param {boolean} [props.pasteCrewDisabled] — gray out paste when target already matches clipboard
 * @param {string} [props.pasteTooltip="Paste Crew"] — tooltip when paste is enabled
 * @param {string} [props.pasteDisabledTooltip="Matches copied crew"] — when paste is disabled
 * @param {boolean} [props.pasteTargetHasExistingCrew=false] — when true, first paste click opens replace confirmation
 * @param {(e: import('react').MouseEvent) => void} [props.onPasteCrewClick]
 * @param {string} [props.emptyMessage="No workers assigned yet"]
 * @param {"default"|"override"} [props.variant="default"]
 * @param {boolean} [props.readOnly=false] When true, the edit (pencil) control is hidden.
 * @param {boolean} [props.crewEditActionsLocked=false] When true, edit and revert controls are disabled (e.g. dispatch-reviewed / locked day).
 */
export default function CrewCard({
  scheduleBlockId,
  title,
  workers = [],
  crewRows,
  readOnly = false,
  crewEditActionsLocked = false,
  editTooltip,
  onEditClick,
  revertTooltip = PLANNING_TOOLTIP_MESSAGES.revertCrewForDay,
  onRevertToDefaultClick,
  onCopyCrewClick,
  showPasteCrew = false,
  pasteCrewDisabled = false,
  pasteTooltip = PLANNING_TOOLTIP_MESSAGES.pasteCrew,
  pasteDisabledTooltip = PLANNING_TOOLTIP_MESSAGES.pasteCrewDisabled,
  pasteTargetHasExistingCrew = false,
  onPasteCrewClick,
  emptyMessage = "Edit to add crew.",
  variant = "default",
}) {
  const scheduleContext = useContext(ScheduleContext);

  const scheduleMode = useMemo(() => {
    if (scheduleBlockId == null) {
      return null;
    }
    if (!scheduleContext) {
      return {
        block: null,
        rows: [],
        canPersistCrew: false,
        currentIds: [],
      };
    }
    const block = scheduleContext.blocks.find((b) =>
      sameScheduleBlockId(b.id, scheduleBlockId)
    );
    if (!block) {
      return {
        block: null,
        rows: [],
        canPersistCrew: false,
        currentIds: [],
      };
    }
    const blockWorkers = Array.isArray(block.worker_assignment)
      ? block.worker_assignment
      : [];
    const rows = blockWorkers.map((w) => ({ worker: w, kind: "present" }));
    const canPersistCrew = /^\d+$/.test(String(block.id));
    return {
      block,
      rows,
      canPersistCrew,
      currentIds: blockWorkers.map((w) => w.id),
    };
  }, [scheduleBlockId, scheduleContext]);

  const isDayLocked = Boolean(
    scheduleMode?.block?.startDate &&
      scheduleContext?.isScheduleDayLocked?.(
        toLocalDateKey(scheduleMode.block.startDate)
      )
  );
  const editActionsLocked = Boolean(crewEditActionsLocked) || isDayLocked;

  const resolvedRows = scheduleMode
    ? scheduleMode.rows
    : (crewRows ?? workers.map((w) => ({ worker: w, kind: "present" })));

  const resolvedOnEdit =
    onEditClick ??
    (scheduleMode?.block && scheduleMode.canPersistCrew && scheduleContext?.handleOpenScopeCrewModal
      ? (e) => {
        e.stopPropagation();
        scheduleContext.handleOpenScopeCrewModal(
          scheduleMode.block,
          scheduleMode.block.startDate
        );
      }
      : undefined);

  const resolvedOnCopy =
    onCopyCrewClick ??
    (scheduleMode?.block && scheduleContext?.copyScheduleCrew
      ? (e) => {
        e.stopPropagation();
        scheduleContext.copyScheduleCrew(scheduleMode.currentIds);
      }
      : undefined);

  const clip = scheduleContext?.scheduleCrewClipboard ?? null;
  const showPasteFromContext =
    Boolean(scheduleMode?.canPersistCrew) &&
    Boolean(scheduleContext?.pasteScheduleCrew) &&
    clip != null &&
    Array.isArray(clip.workerIds);

  const pasteMatchesContext =
    showPasteFromContext &&
    scheduleCrewIdListsEqual(clip.workerIds, scheduleMode.currentIds);

  const resolvedShowPaste = scheduleMode
    ? showPasteFromContext
    : showPasteCrew;
  const resolvedPasteDisabled = scheduleMode
    ? pasteMatchesContext
    : pasteCrewDisabled;
  const pasteCrewActionDisabled = resolvedPasteDisabled || editActionsLocked;
  const resolvedPasteTargetHasCrew = scheduleMode
    ? scheduleMode.currentIds.length > 0
    : pasteTargetHasExistingCrew;

  const resolvedOnPaste =
    onPasteCrewClick ??
    (scheduleMode?.block &&
      scheduleMode.canPersistCrew &&
      scheduleContext?.pasteScheduleCrew
      ? async (e) => {
        e?.stopPropagation?.();
        await scheduleContext.pasteScheduleCrew({
          mode: "block",
          blockId: scheduleMode.block.id,
          day: toLocalCalendarDay(scheduleMode.block.startDate),
        });
      }
      : undefined);

  const resolvedEditTooltip =
    scheduleMode && !scheduleMode.canPersistCrew
      ? undefined
      : editTooltip;

  const [pasteConfirmOpen, setPasteConfirmOpen] = useState(false);
  const [pasteConfirmBusy, setPasteConfirmBusy] = useState(false);

  useEffect(() => {
    if (
      !resolvedPasteTargetHasCrew ||
      resolvedPasteDisabled ||
      editActionsLocked ||
      !resolvedShowPaste
    ) {
      setPasteConfirmOpen(false);
    }
  }, [
    resolvedPasteTargetHasCrew,
    resolvedPasteDisabled,
    editActionsLocked,
    resolvedShowPaste,
  ]);
  const showEdit =
    !readOnly && Boolean(resolvedEditTooltip && resolvedOnEdit);
  const showRevert =
    variant === "override" && Boolean(onRevertToDefaultClick);
  const showCopy =
    Boolean(resolvedOnCopy) &&
    (scheduleMode ? resolvedRows.length > 0 : true);
  const showPaste = Boolean(resolvedShowPaste && resolvedOnPaste);
  const showActions = showEdit || showRevert || showCopy || showPaste;

  const panelClass =
    variant === "override" ? "bg-orange-50" : "bg-gray-50";

  const nameSectionClassForRow = (kind) => {
    if (variant !== "override") {
      return "";
    }
    if (kind === "removed") {
      return "bg-red-200 line-through text-gray-900";
    }
    if (kind === "added") {
      return "bg-green-100 text-gray-900";
    }
    return "";
  };

  const iconBtn =
    "inline-flex shrink-0 items-center justify-center rounded p-1 text-gray-600 transition-colors hover:bg-black/5 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:pointer-events-none disabled:opacity-40";

  return (
    <div
      className={`relative overflow-hidden rounded-md ${panelClass}`}
    >
      <div
        className={
          pasteConfirmOpen
            ? "pointer-events-none select-none opacity-40 transition-opacity duration-200"
            : "transition-opacity duration-200"
        }
      >
        <div className="flex items-center justify-between gap-2 bg-black/10 pl-2">
          <p className="text-sm font-semibold text-gray-600">{title}</p>
          {showActions ? (
            <div className="flex shrink-0 items-center gap-0.5">
              {showEdit ? (
                <span
                  className="inline-flex"
                  title={
                    editActionsLocked
                      ? PLANNING_TOOLTIP_MESSAGES.lockedCrewAction
                      : resolvedEditTooltip
                  }
                >
                  <button
                    type="button"
                    className={`${iconBtn} ${editActionsLocked ? "opacity-40" : ""}`}
                    aria-label={
                      editActionsLocked
                        ? PLANNING_TOOLTIP_MESSAGES.lockedCrewAction
                        : resolvedEditTooltip
                    }
                    disabled={editActionsLocked}
                    onClick={(e) => {
                      e.stopPropagation();
                      resolvedOnEdit?.(e);
                    }}
                  >
                    <Pencil className="h-5 w-5" aria-hidden />
                  </button>
                </span>
              ) : null}
              {showRevert ? (
                <span
                  className="inline-flex"
                  title={
                    editActionsLocked
                      ? PLANNING_TOOLTIP_MESSAGES.lockedCrewAction
                      : revertTooltip
                  }
                >
                  <button
                    type="button"
                    className={`${iconBtn} ${editActionsLocked ? "cursor-not-allowed opacity-40" : ""}`}
                    aria-label={
                      editActionsLocked
                        ? PLANNING_TOOLTIP_MESSAGES.lockedCrewAction
                        : revertTooltip
                    }
                    disabled={editActionsLocked}
                    onClick={(e) => {
                      e.stopPropagation();
                      void onRevertToDefaultClick?.(e);
                    }}
                  >
                    <Refresh className="h-5 w-5" aria-hidden />
                  </button>
                </span>
              ) : null}
              {showCopy ? (
                <button
                  type="button"
                  className={iconBtn}
                  title={PLANNING_TOOLTIP_MESSAGES.copyExistingCrew}
                  aria-label={PLANNING_TOOLTIP_MESSAGES.copyExistingCrew}
                  onClick={(e) => {
                    e.stopPropagation();
                    resolvedOnCopy?.(e);
                  }}
                >
                  <DocumentDuplicate className="h-4 w-4" aria-hidden />
                </button>
              ) : null}
              {showPaste ? (
                <span
                  className="inline-flex"
                  title={
                    editActionsLocked
                      ? PLANNING_TOOLTIP_MESSAGES.lockedCrewAction
                      : resolvedPasteDisabled
                        ? pasteDisabledTooltip
                        : pasteTooltip
                  }
                >
                  <button
                    type="button"
                    className={`${iconBtn} ${pasteCrewActionDisabled
                        ? "cursor-not-allowed opacity-40"
                        : ""
                      }`}
                    aria-label={
                      editActionsLocked
                        ? PLANNING_TOOLTIP_MESSAGES.lockedCrewAction
                        : resolvedPasteDisabled
                          ? pasteDisabledTooltip
                          : pasteTooltip
                    }
                    disabled={pasteCrewActionDisabled}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (
                        resolvedPasteTargetHasCrew &&
                        resolvedOnPaste
                      ) {
                        setPasteConfirmOpen(true);
                        return;
                      }
                      resolvedOnPaste?.(e);
                    }}
                  >
                    <ClipboardPaste className="h-4 w-4" aria-hidden />
                  </button>
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
        {resolvedRows.length === 0 ? (
          <p className="mt-1 text-sm text-gray-500 px-2">{emptyMessage}</p>
        ) : (
          <div className="mt-1.5 flex flex-wrap gap-0.5 px-2">
            {resolvedRows.map(({ worker: w, kind = "present" }) => (
              <div
                key={w.id}
                className={`flex min-w-0 max-w-[200px] bg-gray-100 items-center gap-1 rounded-full ${nameSectionClassForRow(kind)} p-0 pr-2 text-sm text-gray-900 ${nameSectionClassForRow(kind)}`}
              >
                <WorkerInitials
                  worker={w}
                  className="shrink-0 mr-0"
                  colorByRole
                  showName
                  append={kind === "added" && `+`}
                />
              </div>
            ))}
          </div>
        )}
      </div>
      <PasteCrewOverwriteOverlay
        visible={pasteConfirmOpen}
        busy={pasteConfirmBusy}
        onCancel={() => {
          setPasteConfirmOpen(false);
        }}
        onConfirm={async () => {
          if (!resolvedOnPaste || pasteCrewActionDisabled) {
            setPasteConfirmOpen(false);
            return;
          }
          setPasteConfirmBusy(true);
          try {
            await resolvedOnPaste({
              stopPropagation() { },
            });
            setPasteConfirmOpen(false);
          } finally {
            setPasteConfirmBusy(false);
          }
        }}
      />
    </div>
  );
}
